/**
 * A QR code of a text (part 36c, FX3: the authenticator app's `otpauth` URI; the user's choice 2026-10-24, an encoder
 * of the app's own rather than a dependency). ISO/IEC 18004 Model 2: byte mode (the text's UTF-8), error correction
 * level M, the smallest version from 1 to 40 that holds it, and the mask the standard's penalty rules score lowest.
 * Pure: a text in, the modules out. The tests check it against `qrencode` and decode its output with `zbarimg`.
 */

/** A QR code's modules: `size` × `size`, row by row, `true` for dark. */
export interface QrCode {
  readonly size: number;
  readonly version: number;
  readonly mask: number;
  readonly modules: readonly (readonly boolean[])[];
}

/**
 * Level M's blocks for each version (index 0 is version 1): the error correction codewords per block, then the blocks
 * and data codewords per block of the first group and of the second (ISO/IEC 18004, table 9).
 */
const BLOCKS_M: readonly (readonly [ecc: number, blocks1: number, data1: number, blocks2: number, data2: number])[] = [
  [10, 1, 16, 0, 0],
  [16, 1, 28, 0, 0],
  [26, 1, 44, 0, 0],
  [18, 2, 32, 0, 0],
  [24, 2, 43, 0, 0],
  [16, 4, 27, 0, 0],
  [18, 4, 31, 0, 0],
  [22, 2, 38, 2, 39],
  [22, 3, 36, 2, 37],
  [26, 4, 43, 1, 44],
  [30, 1, 50, 4, 51],
  [22, 6, 36, 2, 37],
  [22, 8, 37, 1, 38],
  [24, 4, 40, 5, 41],
  [24, 5, 41, 5, 42],
  [28, 7, 45, 3, 46],
  [28, 10, 46, 1, 47],
  [26, 9, 43, 4, 44],
  [26, 3, 44, 11, 45],
  [26, 3, 41, 13, 42],
  [26, 17, 42, 0, 0],
  [28, 17, 46, 0, 0],
  [28, 4, 47, 14, 48],
  [28, 6, 45, 14, 46],
  [28, 8, 47, 13, 48],
  [28, 19, 46, 4, 47],
  [28, 22, 45, 3, 46],
  [28, 3, 45, 23, 46],
  [28, 21, 45, 7, 46],
  [28, 19, 47, 10, 48],
  [28, 2, 46, 29, 47],
  [28, 10, 46, 23, 47],
  [28, 14, 46, 21, 47],
  [28, 14, 46, 23, 47],
  [28, 12, 47, 26, 48],
  [28, 6, 47, 34, 48],
  [28, 29, 46, 14, 47],
  [28, 13, 46, 32, 47],
  [28, 40, 47, 7, 48],
  [28, 18, 47, 31, 48],
];

/** The highest version. */
export const MAX_VERSION = 40;

/** Level M's two bits in the format information. */
const LEVEL_M = 0b00;

/** A version's side, in modules. */
export function sizeOf(version: number): number {
  return version * 4 + 17;
}

/** `version`'s level M blocks. */
function blocksOf(version: number): readonly [number, number, number, number, number] {
  const blocks = BLOCKS_M[version - 1];
  if (blocks === undefined) throw new RangeError(`no QR version ${String(version)}`);
  return blocks;
}

/** The data codewords `version` holds at level M. */
export function dataCodewords(version: number): number {
  const [, blocks1, data1, blocks2, data2] = blocksOf(version);
  return blocks1 * data1 + blocks2 * data2;
}

/** The codewords `version` holds in all (data and error correction), from its modules: what the blocks must add up to. */
export function rawCodewords(version: number): number {
  let modules = (16 * version + 128) * version + 64;
  if (version >= 2) {
    const aligns = Math.floor(version / 7) + 2;
    modules -= (25 * aligns - 10) * aligns - 55;
    if (version >= 7) modules -= 36;
  }
  return Math.floor(modules / 8);
}

/** The bits of a byte segment's character count for `version`. */
function countBits(version: number): number {
  return version <= 9 ? 8 : 16;
}

/** The smallest version that holds `length` bytes, or null for none. */
export function versionFor(length: number): number | null {
  for (let version = 1; version <= MAX_VERSION; version += 1) {
    if (4 + countBits(version) + length * 8 <= dataCodewords(version) * 8) return version;
  }
  return null;
}

/** A typed array's value at `i`, 0 past its end (the generator reads one past its last coefficient). */
function or0(value: number | undefined): number {
  return value ?? 0;
}

/** GF(256) under the polynomial 0x11D: exponent and log tables. */
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
for (let i = 0, x = 1; i < 255; i += 1) {
  EXP[i] = x;
  EXP[i + 255] = x;
  LOG[x] = i;
  x <<= 1;
  if (x & 0x100) x ^= 0x11d;
}

function multiply(a: number, b: number): number {
  return a === 0 || b === 0 ? 0 : or0(EXP[or0(LOG[a]) + or0(LOG[b])]);
}

/** The Reed–Solomon generator of `degree`: the product of (x − α^i) for i below `degree`, its leading 1 left out. */
export function generator(degree: number): Uint8Array {
  const result = new Uint8Array(degree);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i += 1) {
    for (let j = 0; j < degree; j += 1) result[j] = multiply(or0(result[j]), root) ^ or0(result[j + 1]);
    root = multiply(root, 0x02);
  }
  return result;
}

/** The error correction codewords of `data` under `divisor` (the remainder of its division by the generator). */
export function remainder(data: Uint8Array, divisor: Uint8Array): Uint8Array {
  const result = new Uint8Array(divisor.length);
  for (const byte of data) {
    const factor = byte ^ or0(result[0]);
    result.copyWithin(0, 1);
    result[result.length - 1] = 0;
    divisor.forEach((coefficient, i) => {
      result[i] = or0(result[i]) ^ multiply(coefficient, factor);
    });
  }
  return result;
}

/** The data codewords of `bytes` in `version`: mode, count, the bytes, the terminator and the padding (8.4). */
function dataOf(bytes: Uint8Array, version: number): Uint8Array {
  const out = new Uint8Array(dataCodewords(version));
  let length = 0;
  const push = (value: number, bits: number): void => {
    for (let i = bits - 1; i >= 0; i -= 1, length += 1) if ((value >>> i) & 1) out[length >>> 3] = or0(out[length >>> 3]) | (0x80 >>> (length & 7));
  };
  push(0b0100, 4);
  push(bytes.length, countBits(version));
  for (const byte of bytes) push(byte, 8);
  // The terminator (up to four zero bits) and the zero bits to the byte are already zero; the pad bytes alternate.
  for (let i = Math.ceil(Math.min(out.length * 8, length + 4) / 8), pad = 0xec; i < out.length; i += 1, pad ^= 0xec ^ 0x11) out[i] = pad;
  return out;
}

/** The final codewords of `data` in `version`: the blocks with their error correction, interleaved (8.6). */
export function codewordsOf(data: Uint8Array, version: number): Uint8Array {
  const [ecc, blocks1, data1, blocks2, data2] = blocksOf(version);
  const divisor = generator(ecc);
  const blocks: Uint8Array[] = [];
  for (let b = 0, at = 0; b < blocks1 + blocks2; b += 1) {
    const length = b < blocks1 ? data1 : data2;
    blocks.push(data.subarray(at, at + length));
    at += length;
  }
  const corrections = blocks.map((block) => remainder(block, divisor));
  const out: number[] = [];
  for (let i = 0; i < Math.max(data1, data2); i += 1) for (const block of blocks) if (i < block.length) out.push(or0(block[i]));
  for (let i = 0; i < ecc; i += 1) for (const correction of corrections) out.push(or0(correction[i]));
  return Uint8Array.from(out);
}

/** The centres of `version`'s alignment patterns along one side (Annex E). */
export function alignmentCentres(version: number): readonly number[] {
  if (version === 1) return [];
  const count = Math.floor(version / 7) + 2;
  const step = version === 32 ? 26 : Math.ceil((version * 4 + 4) / (count * 2 - 2)) * 2;
  const centres = [6];
  for (let position = sizeOf(version) - 7; centres.length < count; position -= step) centres.splice(1, 0, position);
  return centres;
}

/** The 15 bits of the format information for `mask` at level M, BCH coded and masked (8.9). */
export function formatBits(mask: number): number {
  const data = (LEVEL_M << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i += 1) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  return ((data << 10) | rem) ^ 0x5412;
}

/** The 18 bits of the version information for `version` (7 and up), BCH coded (8.10). */
export function versionBits(version: number): number {
  let rem = version;
  for (let i = 0; i < 12; i += 1) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
  return (version << 12) | rem;
}

/** Whether the module at (`x`, `y`) is dark under `mask` (8.8.1). */
export function masked(mask: number, x: number, y: number): boolean {
  switch (mask) {
    case 0:
      return (x + y) % 2 === 0;
    case 1:
      return y % 2 === 0;
    case 2:
      return x % 3 === 0;
    case 3:
      return (x + y) % 3 === 0;
    case 4:
      return (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0;
    case 5:
      return ((x * y) % 2) + ((x * y) % 3) === 0;
    case 6:
      return (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
    default:
      return (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
  }
}

/** A matrix being drawn, row by row in one array: its modules (1 dark) and which are function patterns (never masked). */
class Matrix {
  readonly size: number;
  readonly dark: Uint8Array;
  readonly fixed: Uint8Array;

  constructor(size: number) {
    this.size = size;
    this.dark = new Uint8Array(size * size);
    this.fixed = new Uint8Array(size * size);
  }

  /** A function pattern's module at (`x`, `y`), inside the matrix. */
  set(x: number, y: number, dark: boolean): void {
    this.dark[y * this.size + x] = dark ? 1 : 0;
    this.fixed[y * this.size + x] = 1;
  }

  isFixed(x: number, y: number): boolean {
    return this.fixed[y * this.size + x] === 1;
  }

  /** The modules as rows of booleans. */
  rows(): boolean[][] {
    return Array.from({ length: this.size }, (_, y) => Array.from(this.dark.subarray(y * this.size, (y + 1) * this.size), (module) => module === 1));
  }
}

/** A finder pattern centred at (`cx`, `cy`) with its separator, as far as it lies inside the matrix. */
function finder(matrix: Matrix, cx: number, cy: number): void {
  for (let dy = -4; dy <= 4; dy += 1) {
    for (let dx = -4; dx <= 4; dx += 1) {
      const ring = Math.max(Math.abs(dx), Math.abs(dy));
      const x = cx + dx;
      const y = cy + dy;
      if (x >= 0 && x < matrix.size && y >= 0 && y < matrix.size) matrix.set(x, y, ring !== 2 && ring !== 4);
    }
  }
}

/** An alignment pattern centred at (`cx`, `cy`). */
function alignment(matrix: Matrix, cx: number, cy: number): void {
  for (let dy = -2; dy <= 2; dy += 1) for (let dx = -2; dx <= 2; dx += 1) matrix.set(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
}

/** The format information for `mask`, in both its copies, and the dark module. */
function drawFormat(matrix: Matrix, mask: number): void {
  const bits = formatBits(mask);
  const bit = (i: number): boolean => ((bits >>> i) & 1) === 1;
  const size = matrix.size;
  for (let i = 0; i <= 5; i += 1) matrix.set(8, i, bit(i));
  matrix.set(8, 7, bit(6));
  matrix.set(8, 8, bit(7));
  matrix.set(7, 8, bit(8));
  for (let i = 9; i < 15; i += 1) matrix.set(14 - i, 8, bit(i));
  for (let i = 0; i < 8; i += 1) matrix.set(size - 1 - i, 8, bit(i));
  for (let i = 8; i < 15; i += 1) matrix.set(8, size - 15 + i, bit(i));
  matrix.set(8, size - 8, true);
}

/** The version information, in both its copies (version 7 and up). */
function drawVersion(matrix: Matrix, version: number): void {
  if (version < 7) return;
  const bits = versionBits(version);
  for (let i = 0; i < 18; i += 1) {
    const dark = ((bits >>> i) & 1) === 1;
    const a = matrix.size - 11 + (i % 3);
    const b = Math.floor(i / 3);
    matrix.set(a, b, dark);
    matrix.set(b, a, dark);
  }
}

/** Every function pattern of `version`: finders, timing, alignment, and the format and version areas reserved. */
function functionPatterns(version: number): Matrix {
  const matrix = new Matrix(sizeOf(version));
  const size = matrix.size;
  for (let i = 0; i < size; i += 1) {
    matrix.set(6, i, i % 2 === 0);
    matrix.set(i, 6, i % 2 === 0);
  }
  finder(matrix, 3, 3);
  finder(matrix, size - 4, 3);
  finder(matrix, 3, size - 4);
  const centres = alignmentCentres(version);
  const last = centres.length - 1;
  centres.forEach((cy, i) => {
    centres.forEach((cx, j) => {
      // The three corners a finder holds take no alignment pattern.
      if ((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0)) return;
      alignment(matrix, cx, cy);
    });
  });
  drawFormat(matrix, 0);
  drawVersion(matrix, version);
  return matrix;
}

/** Places `codewords` in the modules left, in the standard's two-column zigzag from the bottom right (8.7.3). */
function placeData(matrix: Matrix, codewords: Uint8Array): void {
  const size = matrix.size;
  let i = 0;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    const upward = ((right + 1) & 2) === 0;
    for (let vertical = 0; vertical < size; vertical += 1) {
      for (let x = right; x > right - 2; x -= 1) {
        const y = upward ? size - 1 - vertical : vertical;
        // Every module left takes a bit; the bits run out only where the version's remainder bits sit, which stay light.
        if (matrix.isFixed(x, y)) continue;
        matrix.dark[y * size + x] = (or0(codewords[i >>> 3]) >>> (7 - (i & 7))) & 1;
        i += 1;
      }
    }
  }
}

/** `base` with `mask` applied to its data modules, and its format information for it. */
function withMask(base: Matrix, mask: number): Matrix {
  const matrix = new Matrix(base.size);
  matrix.fixed.set(base.fixed);
  for (let y = 0; y < base.size; y += 1) {
    for (let x = 0; x < base.size; x += 1) {
      const at = y * base.size + x;
      matrix.dark[at] = or0(base.dark[at]) ^ (!base.isFixed(x, y) && masked(mask, x, y) ? 1 : 0);
    }
  }
  drawFormat(matrix, mask);
  return matrix;
}

/** N1–N4 (8.8.2): the weights of the four penalties. */
const N1 = 3;
const N2 = 3;
const N3 = 40;
const N4 = 10;

/**
 * N3's count over a line's newest seven runs (`history`, newest first, light and dark in turn, the newest dark run at
 * 1): a dark 1:1:3:1:1 with a light run of four on one side and at least one on the other, once for each side.
 */
function finderLike(history: readonly [number, number, number, number, number, number, number]): number {
  const [before, a, b, c, d, e, after] = history;
  if (a === 0 || b !== a || c !== a * 3 || d !== a || e !== a) return 0;
  return (before >= a * 4 && after >= a ? 1 : 0) + (after >= a * 4 && before >= a ? 1 : 0);
}

/** One line's N1 and N3 penalties, `at(i)` its modules; the border beyond each end counts as a light run of `size`. */
function linePenalty(size: number, at: (i: number) => boolean): number {
  let penalty = 0;
  let colour = false;
  let run = 0;
  let history: [number, number, number, number, number, number, number] = [0, 0, 0, 0, 0, 0, 0];
  const pushRun = (length: number): void => {
    // The line's first run takes the light border before it.
    history = [history[0] === 0 ? length + size : length, history[0], history[1], history[2], history[3], history[4], history[5]];
  };
  for (let i = 0; i < size; i += 1) {
    const dark = at(i);
    if (dark === colour) {
      run += 1;
      if (run === 5) penalty += N1;
      else if (run > 5) penalty += 1;
      continue;
    }
    pushRun(run);
    if (!colour) penalty += finderLike(history) * N3;
    colour = dark;
    run = 1;
  }
  // The line's end: a dark run ends, then the light border joins the last light run.
  if (colour) {
    pushRun(run);
    run = 0;
  }
  pushRun(run + size);
  return penalty + finderLike(history) * N3;
}

/** The standard's penalty of `modules` (8.8.2), lower is better. */
export function penalty(modules: readonly (readonly boolean[])[]): number {
  const size = modules.length;
  const flat = modules.flat();
  const dark = (x: number, y: number): boolean => flat[y * size + x] === true;
  let total = 0;
  for (let y = 0; y < size; y += 1) total += linePenalty(size, (x) => dark(x, y));
  for (let x = 0; x < size; x += 1) total += linePenalty(size, (y) => dark(x, y));
  for (let y = 0; y < size - 1; y += 1) {
    for (let x = 0; x < size - 1; x += 1) {
      const c = dark(x, y);
      if (c === dark(x + 1, y) && c === dark(x, y + 1) && c === dark(x + 1, y + 1)) total += N2;
    }
  }
  const count = flat.filter((module) => module).length;
  const area = size * size;
  return total + (Math.ceil(Math.abs(count * 20 - area * 10) / area) - 1) * N4;
}

/** Each of the eight masked codes of `text` with its penalty, in mask order; empty when no version holds it. */
export function maskedCodes(text: string): readonly { readonly code: QrCode; readonly penalty: number }[] {
  const bytes = new TextEncoder().encode(text);
  const version = versionFor(bytes.length);
  if (version === null) return [];
  const base = functionPatterns(version);
  placeData(base, codewordsOf(dataOf(bytes, version), version));
  return Array.from({ length: 8 }, (_, mask) => {
    const modules = withMask(base, mask).rows();
    return { code: { size: modules.length, version, mask, modules }, penalty: penalty(modules) };
  });
}

/**
 * The QR code of `text` at level M with the mask of the lowest penalty (the first of equals), or null when no version
 * holds it (more than 2 331 bytes).
 */
export function qrCode(text: string): QrCode | null {
  let best: { readonly code: QrCode; readonly penalty: number } | null = null;
  for (const option of maskedCodes(text)) if (best === null || option.penalty < best.penalty) best = option;
  return best?.code ?? null;
}

/** The dark modules of `code` as one SVG path, in module units, each row's runs as rectangles (no quiet zone). */
export function qrPath(code: QrCode): string {
  const parts: string[] = [];
  code.modules.forEach((row, y) => {
    let x = 0;
    while (x < code.size) {
      if (row[x] !== true) {
        x += 1;
        continue;
      }
      const start = x;
      while (row[x] === true) x += 1;
      parts.push(`M${String(start)} ${String(y)}h${String(x - start)}v1h-${String(x - start)}z`);
    }
  });
  return parts.join('');
}
